import { app } from "./firebase";
import { store } from "../redux/store";
import axios from "axios";
import { initPixelWithUser } from "../utils/pixel";

import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  getIdToken,
  getIdTokenResult,
  signInWithPopup,
  GoogleAuthProvider,
  deleteUser,
  OAuthProvider,
  signInWithCustomToken,
} from "firebase/auth";

import {
  setAdminLoggedIn,
  setGymOwnerLoggedIn,
  setIdToken,
  setLoggedIn,
  setMemberLoggedIn,
} from "../slices/authSlice";
import { getUser, updateUserProfile, startPhoneVerificationApi, signInWithPhoneApi } from "../api/apiEndpoints";
import {
  emptyBasket,
  setUserData,
  setUserExist,
  setFirebaseUser,
  setMembershipUpgraded,
  setBusinessProfile,
  setBusinessDashboardData,
  setPartnerGymInfo,
  setIsNextGymSelected,
  setUserCanDowngradeToBasic,
  setClientSecretStripe,
  clearAppliedDiscount,
} from "../slices/appSlice";
import { convertToSerializable } from "../utils/utils";
import { UpdateProfile } from "../utils/types";
import {
  getPartnerGymInfoCall,
  getUserMembershipStatusCall,
} from "../api/apiOperations";

const dispatch = store.dispatch;

const provider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");

export const auth = getAuth(app);

export const signUpFirebase = async (
  email: string,
  password: string,
  gymOwnerType?: boolean,
  checkout?: boolean,
  updateUser?: UpdateProfile,
) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential?.user;

    dispatch(setFirebaseUser(convertToSerializable(user)));

    await signInWithEmailAndPassword(auth, email, password);

    const idToken = await getIdToken(user, true);
    await updateUserProfile(updateUser, idToken);
    const userData = await getUser(idToken);

    dispatch(setIdToken(idToken));
    dispatch(setUserExist(true));

    await getUserMembershipStatusCall();
    initPixelWithUser(email, updateUser?.phone);
    // -------- we dont set login status here, we do it affter successfull payment.
    if (!checkout) {
      dispatch(setLoggedIn(true));
      dispatch(setUserData(userData?.data?.user));

      // -------- We validate user type twice just to make sure that is the type that we want
      if (gymOwnerType || userData?.data?.user?.role === "partner"){
        dispatch(setGymOwnerLoggedIn(true));
        await getPartnerGymInfoCall();
      }
      dispatch(setMemberLoggedIn(userData?.data?.user?.role === "member"));
      dispatch(setAdminLoggedIn(userData?.data?.user?.role === "admin"));
    }
    return "success";
  } catch (error: any) {
    // console.log("Error:", error)
  
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    if (error?.code === "auth/email-already-in-use") {
      return "An account already exists with this email address.";
    }    

    if (error?.code) {
      return error.code;
    }
  
    return "An unexpected error occurred. Please try again.";
  }
};

export const loginFirebase = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );

    const user = userCredential?.user;

    dispatch(setFirebaseUser(convertToSerializable(user)));

    const idToken = await getIdToken(user, true);
    const userData = await getUser(idToken);

    dispatch(setIdToken(idToken));
    dispatch(setUserData(userData?.data?.user));
    dispatch(setLoggedIn(true));
    dispatch(setMemberLoggedIn(userData?.data?.user?.role === "member"));
    dispatch(setGymOwnerLoggedIn(userData?.data?.user?.role === "partner"));
    dispatch(setAdminLoggedIn(userData?.data?.user?.role === "admin"));
    dispatch(emptyBasket());
    initPixelWithUser(userData?.data?.user?.email, userData?.data?.user?.phone);
    await getUserMembershipStatusCall();

    if (userData?.data?.user?.role === "partner") {
      // Grab all partner gym information
      await getPartnerGymInfoCall();
    }

    return {
      user,
      role: userData?.data?.user?.role
    };
  } catch (error: any) {
    if (
      error.code === "auth/invalid-email" ||
      error.code === "auth/invalid-credential"
    ) {
      const error = {
        error: true,
        message: "Invalid credentials",
      };
      return { error };
    }
  }
};

const finalizeLogin = async (user: any) => {
  dispatch(setFirebaseUser(convertToSerializable(user)));

  const idToken = await getIdToken(user, true);
  const userData = await getUser(idToken);

  dispatch(setIdToken(idToken));
  dispatch(setUserData(userData?.data?.user));
  dispatch(setLoggedIn(true));
  dispatch(setMemberLoggedIn(userData?.data?.user?.role === "member"));
  dispatch(setGymOwnerLoggedIn(userData?.data?.user?.role === "partner"));
  dispatch(setAdminLoggedIn(userData?.data?.user?.role === "admin"));
  dispatch(emptyBasket());
  initPixelWithUser(userData?.data?.user?.email, userData?.data?.user?.phone);
  await getUserMembershipStatusCall();

  if (userData?.data?.user?.role === "partner") {
    await getPartnerGymInfoCall();
  }

  return { user, role: userData?.data?.user?.role };
};

export const startPhoneVerification = async (phoneNumber: string) => {
  try {
    await startPhoneVerificationApi(phoneNumber);
    return { ok: true };
  } catch {
    return { error: { error: true, message: "Failed to send verification code. Please try again." } };
  }
};

export const phoneSignIn = async (phone: string, otpCode: string) => {
  try {
    const { data } = await signInWithPhoneApi(phone, otpCode);
    const customToken = (data as { customToken: string })?.customToken;
    if (!customToken) {
      return { error: { error: true, message: "Invalid or expired code. Please try again." } };
    }
    const userCredential = await signInWithCustomToken(auth, customToken);
    return await finalizeLogin(userCredential.user);
  } catch (err: any) {
    const backendMessage = err?.response?.data?.error;
    const noAccount =
      typeof backendMessage === "string" &&
      backendMessage.toLowerCase().includes("no account");
    return {
      error: {
        error: true,
        noAccount,
        message:
          backendMessage || "Invalid or expired code. Please try again.",
      },
    };
  }
};

export const userObserver = (user: any) =>
  onAuthStateChanged(auth, (user) => {
    if (user) {
      // User is signed in, see docs for a list of available properties
      // https://firebase.google.com/docs/reference/js/auth.user
      const uid = user.uid;
      // ...
    } else {
      // User is signed out
      // ...
    }
  });

export const loginWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, provider);
    // This gives you a Google Access Token. You can use it to access the Google API.
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const token = credential?.accessToken;
    // The signed-in user info.
    const user = result.user;
    dispatch(setFirebaseUser(convertToSerializable(user)));

    const idToken = await getIdToken(user, true);
    dispatch(setIdToken(idToken));
    const userData = await getUser(idToken);

    if (token) dispatch(setLoggedIn(true));
    // IdP data available using getAdditionalUserInfo(result)
    // ...

    dispatch(setUserData(userData?.data?.user));
    dispatch(setMemberLoggedIn(userData?.data?.user?.role === "member"));
    dispatch(setGymOwnerLoggedIn(userData?.data?.user?.role === "partner"));
    dispatch(setAdminLoggedIn(userData?.data?.user?.role === "admin"));
    dispatch(emptyBasket());
    initPixelWithUser(userData?.data?.user?.email, userData?.data?.user?.phone);
    await getUserMembershipStatusCall();

    if (userData?.data?.user?.role === "partner") {
      // Grab all partner gym information
      await getPartnerGymInfoCall();
    }
  } catch (error: any) {
    // Handle Errors here.
    const errorCode = error.code;
    const errorMessage = error.message;
    // The email of the user's account used.
    const email = error.customData.email;
    // The AuthCredential type that was used.
    const credential = GoogleAuthProvider.credentialFromError(error);
    // ...
  }
};

export const loginWithApple = async () => {
  try {
    const result = await signInWithPopup(auth, appleProvider);

    // Apple credential
    const credential = OAuthProvider.credentialFromResult(result);
    const token = credential?.accessToken;
    // The signed-in user info.
    const user = result?.user;
    dispatch(setFirebaseUser(convertToSerializable(user)));

    const idToken = await getIdToken(user, true);
    dispatch(setIdToken(idToken));
    const userData = await getUser(idToken);

    if (token) dispatch(setLoggedIn(true));
    // IdP data available using getAdditionalUserInfo(result)
    // ...

    dispatch(setUserData(userData?.data?.user));
    dispatch(setMemberLoggedIn(userData?.data?.user?.role === "member"));
    dispatch(setGymOwnerLoggedIn(userData?.data?.user?.role === "partner"));
    dispatch(setAdminLoggedIn(userData?.data?.user?.role === "admin"));
    dispatch(emptyBasket());
    initPixelWithUser(userData?.data?.user?.email, userData?.data?.user?.phone);
    await getUserMembershipStatusCall();

    if (userData?.data?.user?.role === "partner") {
      // Grab all partner gym information
      await getPartnerGymInfoCall();
    }
  } catch (error: any) {
    // Handle Errors here.
    const errorCode = error.code;
    const errorMessage = error.message;
    // The email of the user's account used.
    const email = error.customData.email;
    // The AuthCredential type that was used.
    const credential = OAuthProvider.credentialFromError(error);
    // ...
  }
};

export const resetPassFirebase = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    const errorCode = error.code;
    const errorMessage = error.message;
  }
};

export const logoutFirebase = async () => {
  try {
    await auth.signOut();
    dispatch(setLoggedIn(false));
    dispatch(setGymOwnerLoggedIn(false));
    dispatch(setMemberLoggedIn(false));
    dispatch(setAdminLoggedIn(false));
    dispatch(setIdToken(""));
    dispatch(setUserExist(false));
    dispatch(setFirebaseUser({}));
    dispatch(setMembershipUpgraded(false));
    dispatch(setBusinessProfile({}));
    dispatch(setBusinessDashboardData([]));
    dispatch(setPartnerGymInfo({}));
    dispatch(setUserData({}));
    dispatch(setIsNextGymSelected(false));
    dispatch(setUserCanDowngradeToBasic(false));
    dispatch(setClientSecretStripe(""));
    dispatch(clearAppliedDiscount());
  } catch (error: any) {
    const errorCode = error.code;
    const errorMessage = error.message;
  }
};

export const getIdTokenFirebase = async () => {
  try {
    const idToken = await auth.currentUser?.getIdToken(true);
    dispatch(setIdToken(idToken));
    axios.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;
    return getIdTokenResult;
  } catch (error: any) {
    const errorCode = error.code;
    const errorMessage = error.message;
  }
};

export const deleteUserFirebase = () => {
  const user = auth.currentUser;
  try {
    deleteUser(user as any)
      .then(() => {
        logoutFirebase();
      })
      .catch((error) => {});
  } catch (error: any) {
    return error;
  }
};
